import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { apiService } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { useAppContext } from '../../contexts/AppContext';
import { WhatsAppPhoneCardData } from './WhatsAppPhoneCard';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { saveWhatsAppFlowAttachment } from '../../utils/whatsapp-flow-attachment';

interface WhatsAppPhoneSettingsProps {
  phone: WhatsAppPhoneCardData | null;
  onSave?: (settings: PhoneSettingsData) => void;
}

export interface PhoneSettingsData {
  phoneId: string;
  assignToDepartment?: string;
  flowId?: string;
}

interface FlowSummary {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

export const WhatsAppPhoneSettings: React.FC<WhatsAppPhoneSettingsProps> = ({
  phone,
  onSave,
}) => {
  const styles = useThemedStyles(getStyles);
  const { currentBusiness } = useAppStore();
  const { showNotification, navigateTo } = useAppContext();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingFlows, setIsLoadingFlows] = useState(false);
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [showFlowSelector, setShowFlowSelector] = useState(false);

  const [settingsId, setSettingsId] = useState<string | undefined>(undefined);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [settingsLoadFailed, setSettingsLoadFailed] = useState(false);
  const [settings, setSettings] = useState<PhoneSettingsData>({
    phoneId: phone?.id || '',
  });

  useEffect(() => {
    loadFlows();
  }, []);

  // Load channel settings + any attached flow when phone changes
  useEffect(() => {
    setSettingsId(undefined);
    setSettings({ phoneId: phone?.id || '' });
    setShowFlowSelector(false);
    setSettingsLoadFailed(false);
    if (!phone || !currentBusiness?._id) return;
    setIsLoadingSettings(true);
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getChannelSettings({
          businessId: currentBusiness._id,
          channel: 'whatsapp',
          channelIdentifier: phone.id,
        });
        if (cancelled) return;
        if (res.statusCode !== 1000) throw new Error('Failed to load channel settings');
        if (res.statusCode === 1000 && res.data) {
          const d = res.data as any;
          setSettingsId(d._id);
          setSettings(prev => ({
            ...prev,
            phoneId: phone.id,
            assignToDepartment: d.assignToDepartment,
          }));

          if (d._id) {
            const attachRes = await apiService.getFlowAttachments({ scope: 'channel_settings', attachedTo: d._id, isActive: true, triggerType: 'message_received' });
            if (attachRes.statusCode !== 1000 || !Array.isArray(attachRes.data)) throw new Error('Failed to load workflow attachments');
            if (!cancelled && attachRes.statusCode === 1000 && Array.isArray(attachRes.data) && attachRes.data.length > 0) {
              const attachment = attachRes.data.find((a: any) =>
                (!a.channel || a.channel === 'whatsapp') &&
                (!a.channelIdentifier || a.channelIdentifier === phone.id));
              if (attachment) setSettings(prev => ({ ...prev, flowId: attachment.flowId }));
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          setSettingsLoadFailed(true);
          console.error('Error loading channel settings:', e);
          showNotification('error', 'Error', 'Failed to load channel settings. Reopen this phone to retry.');
        }
      } finally {
        if (!cancelled) setIsLoadingSettings(false);
      }
    })();
    return () => { cancelled = true; };
  }, [phone?.id, currentBusiness?._id]);

  const loadFlows = async () => {
    try {
      setIsLoadingFlows(true);
      const response = await apiService.getFlows({ isActive: true, limit: 100 });
      if (response.statusCode === 1000 && response.data?.items) {
        setFlows(response.data.items);
      }
    } catch (error: any) {
      console.error('Error loading flows:', error);
    } finally {
      setIsLoadingFlows(false);
    }
  };

  const handleSelectFlow = (flowId: string) => {
    setSettings(prev => ({ ...prev, flowId: prev.flowId === flowId ? undefined : flowId }));
    setShowFlowSelector(false);
  };

  const handleSave = async () => {
    if (!phone || !currentBusiness?._id || isLoadingSettings || settingsLoadFailed) return;
    try {
      setIsSaving(true);

      // A FlowAttachment needs a real channel-settings document to point at. The business
      // reaching this screen already means the channel itself is connected/enabled (that's
      // gated upstream in subscription setup) — this upsert just guarantees the settings
      // ROW exists so it has an _id, transparently, without a separate "enable" step.
      let effectiveSettingsId = settingsId;
      if (!effectiveSettingsId) {
        const upsertRes = await apiService.upsertChannelSettings({
          businessId: currentBusiness._id,
          channel: 'whatsapp',
          channelIdentifier: phone.id,
          assignToDepartment: settings.assignToDepartment,
        });
        if (upsertRes.statusCode === 1000 && upsertRes.data?._id) {
          effectiveSettingsId = upsertRes.data._id;
          setSettingsId(effectiveSettingsId);
        }
      }

      if (!effectiveSettingsId) throw new Error('Failed to save channel settings');
      await saveWhatsAppFlowAttachment(apiService, effectiveSettingsId, phone.id, settings.flowId);

      if (onSave) onSave(settings);
    } catch (error: any) {
      console.error('Error saving phone settings:', error);
      showNotification('error', 'Error', error.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (!phone) {
    return (
      <View style={styles.emptyState}>
        <Ionicons name="call-outline" size={64} color={colors.text.secondary} />
        <Text style={styles.emptyStateText}>Select a phone number to configure settings</Text>
      </View>
    );
  }

  const selectedFlow = flows.find(f => f._id === settings.flowId);

  if (isLoadingSettings) return <ActivityIndicator color={colors.primary} />;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      {/* Assign to Department Section */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Assign to Department</Text>
        <Text style={styles.sectionDescription}>
          Departments feature coming soon. You'll be able to assign conversations to specific departments.
        </Text>
        <View style={styles.comingSoonBadge}>
          <Ionicons name="time-outline" size={16} color={colors.text.secondary} />
          <Text style={styles.comingSoonText}>Coming Soon</Text>
        </View>
      </View>

      {/* Attached Flow Section */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionLabel}>Attached Flow</Text>
          <TouchableOpacity onPress={() => navigateTo('flows-list')} activeOpacity={0.7}>
            <Text style={styles.manageFlowsLink}>Manage Flows</Text>
          </TouchableOpacity>
        </View>
        <Text style={styles.sectionDescription}>
          When a contact reaches this number, this flow runs — send messages, ask questions, assign the conversation, and more, all configured in the flow itself.
        </Text>

        <View style={styles.flowSelector}>
          <TouchableOpacity
            style={styles.flowButton}
            onPress={() => setShowFlowSelector(!showFlowSelector)}
            activeOpacity={0.7}
          >
            <Ionicons name="git-branch-outline" size={20} color={colors.primary} />
            <Text style={styles.flowButtonText}>
              {selectedFlow ? selectedFlow.name : 'Select a Flow'}
            </Text>
            <Ionicons
              name={showFlowSelector ? 'chevron-up' : 'chevron-down'}
              size={20}
              color={colors.text.secondary}
            />
          </TouchableOpacity>

          {selectedFlow && (
            <TouchableOpacity
              style={styles.removeFlowButton}
              onPress={() => setSettings(prev => ({ ...prev, flowId: undefined }))}
            >
              <Ionicons name="close-circle" size={20} color={colors.error} />
            </TouchableOpacity>
          )}
        </View>

        {showFlowSelector && (
          <View style={styles.selectorContainer}>
            {isLoadingFlows ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color={colors.primary} />
                <Text style={styles.loadingText}>Loading flows...</Text>
              </View>
            ) : flows.length > 0 ? (
              flows.map((flow) => {
                const isSelected = settings.flowId === flow._id;
                return (
                  <TouchableOpacity
                    key={flow._id}
                    style={[styles.flowItem, isSelected && styles.flowItemSelected]}
                    onPress={() => handleSelectFlow(flow._id)}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="git-branch-outline" size={20} color={colors.primary} />
                    <View style={styles.flowItemInfo}>
                      <Text style={styles.flowItemName}>{flow.name}</Text>
                      {flow.description && (
                        <Text style={styles.flowItemDescription} numberOfLines={1}>
                          {flow.description}
                        </Text>
                      )}
                    </View>
                    {isSelected && (
                      <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                );
              })
            ) : (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>No flows yet</Text>
                <TouchableOpacity onPress={() => navigateTo('flow-edit')} style={styles.createFlowLink}>
                  <Text style={styles.createFlowLinkText}>Create a Flow</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Save Button */}
      <View style={styles.buttonContainer}>
        <TouchableOpacity
          style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
          onPress={handleSave}
          activeOpacity={0.7}
          disabled={isSaving || isLoadingSettings || settingsLoadFailed}
        >
          {isSaving ? (
            <ActivityIndicator size="small" color={colors.white} />
          ) : (
            <Text style={styles.saveButtonText}>Save Settings</Text>
          )}
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  contentContainer: {
    padding: spacing.lg,
  },
  section: {
    marginBottom: spacing.xl,
    padding: spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  sectionLabel: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  sectionDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginBottom: spacing.md,
    lineHeight: 20,
  },
  comingSoonBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    padding: spacing.sm,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    alignSelf: 'flex-start',
  },
  comingSoonText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
  },
  manageFlowsLink: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  selectorContainer: {
    marginTop: spacing.md,
    maxHeight: 300,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.surface,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    }),
  },
  loadingContainer: {
    padding: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  flowSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  flowButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.surface,
  },
  flowButtonText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  removeFlowButton: {
    padding: spacing.sm,
  },
  flowItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  flowItemSelected: {
    backgroundColor: `${String(colors.primary)}08`,
  },
  flowItemInfo: {
    flex: 1,
  },
  flowItemName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  flowItemDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: 2,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 2,
  },
  emptyStateText: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  emptyContainer: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
  },
  createFlowLink: {
    padding: spacing.xs,
  },
  createFlowLinkText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  buttonContainer: {
    marginTop: spacing.lg,
  },
  saveButton: {
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: spacing.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.7,
  },
  saveButtonText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
});
